import React from "react";
import BrandLogo from "@/components/BrandLogo";
import NotificationStream from "@/context/NotificationStream";
import { UnreadProvider } from "@/context/UnreadContext";
import { AdminPendingProvider } from "@/context/AdminPendingContext";
import CommunityChat from "@/components/CommunityChat";
import PwaInstall from "@/components/PwaInstall";
import IosInstallHint from "@/components/IosInstallHint";
import OnboardingTour from "@/components/OnboardingTour";
import Sidebar from "@/components/shell/Sidebar";
import BottomNav from "@/components/shell/BottomNav";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

/**
 * AppShell (Layout).
 * - Desktop (≥1024px): left Sidebar + main content grid.
 * - Mobile/tablet (<1024px): sticky top-bar with brand + persistent BottomNav
 *   (Home / Education / Performance / Profile [+ Admin]).
 *
 * Real-time notifications are mounted here so the SSE stream lives for the
 * whole authenticated session.
 */
export default function AppShell({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <UnreadProvider>
      <AdminPendingProvider>
        <div className="min-h-screen bg-white text-slate-900" style={{ minHeight: "100dvh", overflowX: "clip" }}>
          <Toaster position="top-right" richColors closeButton />
          {user && <NotificationStream />}

          {/* Top bar (mobile + tablet only) — brand-only, no hamburger. */}
          <header className="lg:hidden sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 px-5 py-3 flex items-center justify-center">
            <BrandLogo size={30} />
          </header>

          <div className="lg:grid lg:grid-cols-[280px_1fr]">
            <Sidebar user={user} />
            {/* pb-24 keeps last row above the fixed bottom-nav on mobile. */}
            <main className="min-w-0 pb-24 lg:pb-0">{children}</main>
          </div>

          {/* Persistent bottom navigation (mobile only). */}
          <BottomNav isAdmin={isAdmin} />

          <CommunityChat />
          <PwaInstall />
          <IosInstallHint />
          <OnboardingTour user={user} />
        </div>
      </AdminPendingProvider>
    </UnreadProvider>
  );
}
